import React from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { TabsLayout } from '@app/layouts'
import { Header, PermissionWrapper } from '@app/components'
import { LntButton, LntDropdown } from '@app/components/base'
import { Icon } from '@app/components/util'
import { getExportFilesDropdownMenu } from '@app/utils'

import { useNotification, useToggle } from '@app/hooks'

import { BUTTON_TYPE, SIZE } from '@app/constants'
import { CONTRACTORS_KEY } from '@app/constants/queryKeys'
import { PERMISSION } from '@app/constants/permission'
import { FILE_TYPES } from '@app/api/constants'
import { ICON_NAME } from '@app/constants/icon'

import { createContractor, postContractorsCluster } from '@app/api/routes/contractors'

import { exportContractorsClusterFile } from '@app/utils/export'

import { NewConnectionContractorsModal } from '@app/modals/NewConnectionContractorsModal'
import { CommonContractorDrawer } from '@app/drawers/CommonContractorDrawer/CommonContractorDrawer'

import { ConnectionsContractorsTab } from './Tabs/ConnectionsContractorsTab'
import { ContractorsTab } from './Tabs/ContractorsTab'

import type { TabsLayoutItem, TabToolbars } from '@app/layouts/TabsLayout/types'
import type { PostContractorsClusterData, CommonContractorsData } from '@app/api/routes/contractors/types'
import type { FileType } from '@app/types'

const TABS_KEYS = {
  connectionsContractorsTab: 'connectionsContractorsTab',
  contractorsTab: 'contractorsTab'
} as const

const tabs: TabsLayoutItem[] = [
  {
    label: 'Связи подрядчиков',
    key: TABS_KEYS.connectionsContractorsTab,
    content: <ConnectionsContractorsTab />
  },

  {
    label: 'Подрядчики',
    key: TABS_KEYS.contractorsTab,
    content: <ContractorsTab />,
    disabled: true
  }
]

const title = 'Справочник подрядчиков'

export const DirectoryContractorsPage: React.FC = () => {
  const {
    value: isOpenNewLinkContractorsModal,
    setUntoggled: handleCloseNewLinkContractorsModal,
    setToggled: handleOpenNewLinkContractorsModal
  } = useToggle()

  const {
    value: isOpenContractorDrawer,
    setUntoggled: handleCloseContractorDrawer,
    setToggled: handleOpenContractorDrawer
  } = useToggle()

  const buttonLoadingExportAllToggle = useToggle()

  const notification = useNotification()
  const queryClient = useQueryClient()

  const createConnectionMutation = useMutation({
    mutationFn: postContractorsCluster,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [CONTRACTORS_KEY.contractorsClustersItems] })

      notification.success({
        message: 'Связь создана',
        description: 'Успешное создание связи'
      })
    }
  })

  const handleCreateConnection = (data: PostContractorsClusterData): void => {
    createConnectionMutation.mutate(data, { onSettled: handleCloseNewLinkContractorsModal })
  }

  const createContractorMutation = useMutation({
    mutationFn: createContractor,
    onSuccess: async () => await queryClient.invalidateQueries({ queryKey: [CONTRACTORS_KEY.contractorsItems] })
  })

  const handleCreateContractor = (data: CommonContractorsData): void => {
    createContractorMutation.mutate(data, { onSettled: handleCloseContractorDrawer })
  }

  const handleExportContractorsClusterFile = async (fileType: FileType): Promise<void> => {
    buttonLoadingExportAllToggle.setToggled()

    const fileName = 'Связи_подрядчиков'

    try {
      await exportContractorsClusterFile(fileName, { file: fileType })
    } finally {
      buttonLoadingExportAllToggle.setUntoggled()
    }
  }

  const exportAllDropdownItems = getExportFilesDropdownMenu(
    'Связи подрядчиков',
    [FILE_TYPES.xlsx, FILE_TYPES.csv],
    handleExportContractorsClusterFile
  )

  const tabsSubheaderToolbars: TabToolbars = {
    [TABS_KEYS.connectionsContractorsTab]: [
      <PermissionWrapper
        key='export'
        requiredPermissions={ [PERMISSION.configureMonitoringParameters] }
      >
        <LntDropdown
          trigger={ ['click'] }
          items={ exportAllDropdownItems }
        >
          <LntButton
            type={ BUTTON_TYPE.secondary }
            size={ SIZE.small }
            loading={ buttonLoadingExportAllToggle.value }
          >
            Экспорт всех
            <Icon name={ ICON_NAME.arrowDown } />
          </LntButton>
        </LntDropdown>
      </PermissionWrapper>,

      <PermissionWrapper
        key='add-link'
        requiredPermissions={ [PERMISSION.configureMonitoringParameters] }
      >
        <LntButton
          type={ BUTTON_TYPE.primary }
          size={ SIZE.small }
          onClick={ handleOpenNewLinkContractorsModal }
        >
          Добавить связь
        </LntButton>
      </PermissionWrapper>
    ],
    [TABS_KEYS.contractorsTab]: [
      <LntButton
        key='export'
        type={ BUTTON_TYPE.secondary }
        size={ SIZE.small }
        onClick={ handleOpenContractorDrawer }
      >
        Добавить подрядчика
      </LntButton>
    ]
  }

  return (
    <>
      <Header controlProps={ { title } } />

      <TabsLayout
        tabs={ tabs }
        tabToolbars={ tabsSubheaderToolbars }
      />

      <NewConnectionContractorsModal
        open={ isOpenNewLinkContractorsModal }
        onCancel={ handleCloseNewLinkContractorsModal }
        onSubmit={ handleCreateConnection }
      />

      <CommonContractorDrawer
        title='Добавить подрядчика'
        open={ isOpenContractorDrawer }
        onClose={ handleCloseContractorDrawer }
        onSubmit={ handleCreateContractor }
      />
    </>
  )
}
